/**
 * 霓虹蛇境 (Neon Snake Realm) - 游戏状态 Reducer 模块
 *
 * 本模块从 App.tsx 中提取了所有游戏状态管理逻辑，
 * 包括 gameReducer 纯函数及其辅助函数。
 *
 * 模块职责：
 * - 创建初始游戏状态
 * - 处理所有 GameAction 类型的状态转换
 * - 提供蛇移动、食物生成、道具收集、碰撞响应等辅助函数
 * - 管理得分、连击、道具效果计时器等游戏逻辑
 *
 * 所有函数均为纯函数或具有明确边界的工厂函数，
 * 不直接操作 DOM 或依赖 React 组件上下文。
 */

import type {
  GameState,
  GameAction,
  ActiveEffect,
  Obstacle,
  Position,
  Food,
  GridConfig,
  Snake,
} from '@/types/game';
import { GameStatus, PowerUpType } from '@/types/game';
import {
  GAME_CONFIG,
  DIRECTION_VECTORS,
  COLOR_SCHEME,
} from '@/game/constants';
import {
  positionsEqual,
  isPositionOnSnake,
  isOppositeDirection,
  checkWallCollision,
  checkSelfCollision,
  checkFoodCollision,
  checkPowerUpCollision,
  checkObstacleCollision,
} from '@/game/collision';
import {
  spawnParticles,
  limitParticles,
  spawnDeathParticles,
} from '@/game/particles';

// ============================================================================
// 工具函数
// ============================================================================

/**
 * 生成指定范围内的随机整数 [min, max]（两端均包含）。
 *
 * @param min - 范围最小值（包含）
 * @param max - 范围最大值（包含）
 * @returns 在 [min, max] 范围内的随机整数
 */
export function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * 从只读数组中随机选取一个元素。
 *
 * @param arr - 只读数组
 * @returns 随机选取的数组元素
 */
export function randomPick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * 从 localStorage 读取最高分，若不存在或解析失败则返回 0。
 *
 * 使用 try-catch 处理 localStorage 不可用的场景（如隐私模式、存储已满）。
 *
 * @returns 历史最高分，默认为 0
 */
export function loadHighScore(): number {
  try {
    const stored = localStorage.getItem('neon-snake-high-score');
    if (stored !== null) {
      const parsed = Number.parseInt(stored, 10);
      return Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
    }
  } catch {
    // localStorage 不可用时静默失败
  }
  return 0;
}

/**
 * 将最高分保存到 localStorage。
 *
 * 使用 try-catch 处理 localStorage 不可用的场景。
 *
 * @param score - 要保存的分数
 */
export function saveHighScore(score: number): void {
  try {
    localStorage.setItem('neon-snake-high-score', String(score));
  } catch {
    // localStorage 不可用时静默失败
  }
}

// ============================================================================
// 食物与道具生成
// ============================================================================

/**
 * 在空白区域随机生成食物位置。
 *
 * 使用拒绝采样算法：在网格范围内随机选取位置，
 * 若与蛇身、障碍物重叠则重新选取，直到找到空白位置。
 * 设置最大尝试次数以防止极端情况下的无限循环
 * （蛇占满整个网格时，返回最后一个位置）。
 *
 * @param snakeBody - 当前蛇身坐标数组
 * @param grid - 游戏网格配置
 * @param obstacles - 当前场景中的障碍物列表
 * @returns 新生成的食物实体
 */
export function generateRandomFood(
  snakeBody: Position[],
  grid: GridConfig,
  obstacles: Obstacle[] = []
): Food {
  let position: Position;
  let attempts = 0;
  const maxAttempts = grid.width * grid.height;

  do {
    position = {
      x: randomInt(0, grid.width - 1),
      y: randomInt(0, grid.height - 1),
    };
    attempts++;
    if (attempts > maxAttempts) {
      // 极端情况：蛇占满整个网格（玩家胜利）
      // 返回一个可能重叠的位置，由碰撞检测处理
      break;
    }
  } while (
    isPositionOnSnake(position, snakeBody) ||
    obstacles.some((obs) => positionsEqual(position, obs.position))
  );

  return { position, type: 'normal' };
}

/**
 * 在空白区域随机生成道具位置。
 *
 * 道具位置不能与蛇身、食物或障碍物位置重叠。
 * 使用拒绝采样算法，设置最大尝试次数防止无限循环。
 *
 * @param snakeBody - 当前蛇身坐标数组
 * @param foodPosition - 当前食物位置
 * @param grid - 游戏网格配置
 * @param obstacles - 当前场景中的障碍物列表
 * @returns 道具的网格位置
 */
export function generateRandomPowerUpPosition(
  snakeBody: Position[],
  foodPosition: Position,
  grid: GridConfig,
  obstacles: Obstacle[] = []
): Position {
  let position: Position;
  let attempts = 0;
  const maxAttempts = grid.width * grid.height;

  do {
    position = {
      x: randomInt(0, grid.width - 1),
      y: randomInt(0, grid.height - 1),
    };
    attempts++;
    if (attempts > maxAttempts) {
      break;
    }
  } while (
    isPositionOnSnake(position, snakeBody) ||
    positionsEqual(position, foodPosition) ||
    obstacles.some((obs) => positionsEqual(position, obs.position))
  );

  return position;
}

/**
 * 从 PowerUpType 枚举中随机选取一个道具类型。
 *
 * @returns 随机选取的道具类型
 */
export function randomPowerUpType(): PowerUpType {
  const types = Object.values(PowerUpType);
  return randomPick(types);
}

// ============================================================================
// 障碍物生成
// ============================================================================

/**
 * 在空白区域随机生成障碍物。
 *
 * 障碍物不能与蛇身、食物、道具或已有障碍物重叠。
 * 使用拒绝采样算法，设置最大尝试次数防止无限循环。
 * 如果生成失败（找不到空位），则放弃生成该障碍物。
 *
 * @param count - 要生成的障碍物数量
 * @param snakeBody - 当前蛇身坐标数组
 * @param foodPosition - 当前食物位置
 * @param powerUpPosition - 当前道具位置（可为 null）
 * @param existingObstacles - 已有障碍物列表
 * @param grid - 游戏网格配置
 * @returns 新生成的障碍物数组（可能少于 count 如果生成失败）
 */
export function generateRandomObstacles(
  count: number,
  snakeBody: Position[],
  foodPosition: Position,
  powerUpPosition: Position | null,
  existingObstacles: Obstacle[],
  grid: GridConfig
): Obstacle[] {
  const newObstacles: Obstacle[] = [];
  const maxAttempts = GAME_CONFIG.obstacleSpawnMaxAttempts;

  for (let i = 0; i < count; i++) {
    let position: Position | null = null;
    let attempts = 0;

    while (attempts < maxAttempts) {
      const candidate: Position = {
        x: randomInt(0, grid.width - 1),
        y: randomInt(0, grid.height - 1),
      };

      // 检查是否与蛇身重叠
      if (isPositionOnSnake(candidate, snakeBody)) {
        attempts++;
        continue;
      }

      // 检查是否与食物重叠
      if (positionsEqual(candidate, foodPosition)) {
        attempts++;
        continue;
      }

      // 检查是否与道具重叠
      if (powerUpPosition !== null && positionsEqual(candidate, powerUpPosition)) {
        attempts++;
        continue;
      }

      // 检查是否与已有障碍物重叠
      const allObstacles = [...existingObstacles, ...newObstacles];
      if (allObstacles.some((obs) => positionsEqual(candidate, obs.position))) {
        attempts++;
        continue;
      }

      // 找到空位
      position = candidate;
      break;
    }

    // 如果找到空位则添加障碍物，否则放弃生成
    if (position !== null) {
      newObstacles.push({ position });
    }
  }

  return newObstacles;
}

// ============================================================================
// 初始状态工厂
// ============================================================================

/**
 * 创建游戏的初始状态。
 *
 * 蛇身位于网格中央，初始方向向右，身体向左延伸。
 * 食物随机生成在空白区域。
 * 最高分从 localStorage 加载。
 * 网格 cellSize 初始为 0，将在渲染时根据画布大小动态计算。
 *
 * @returns 完整的初始 GameState
 */
export function createInitialState(): GameState {
  const grid: GridConfig = {
    width: GAME_CONFIG.desktopGridWidth,
    height: GAME_CONFIG.desktopGridHeight,
    cellSize: 0, // 将在渲染时根据画布大小动态计算
  };

  const centerX = Math.floor(grid.width / 2);
  const centerY = Math.floor(grid.height / 2);

  // 初始蛇身：头部在中心，身体向左延伸
  const body: Position[] = [];
  for (let i = 0; i < GAME_CONFIG.initialSnakeLength; i++) {
    body.push({ x: centerX - i, y: centerY });
  }

  const snake: Snake = {
    body,
    direction: DIRECTION_VECTORS.RIGHT,
    nextDirection: DIRECTION_VECTORS.RIGHT,
  };

    // 生成不在蛇身和障碍物上的随机食物位置
  const food = generateRandomFood(body, grid, []);

    return {
    status: GameStatus.Idle,
    snake,
    food,
    powerUpItem: null,
    activeEffects: [],
    score: {
      current: 0,
      highScore: loadHighScore(),
      combo: 0,
      lastEatTime: 0,
    },
    grid,
    particles: [],
    obstacles: [],
    lives: GAME_CONFIG.initialLives,
  };
}

// ============================================================================
// 蛇移动与穿墙
// ============================================================================

/**
 * 移动蛇：应用缓冲方向，计算新头部，移除尾部。
 *
 * 移动逻辑：
 * 1. 使用缓冲的 nextDirection 作为实际移动方向
 * 2. 在当前头部前方计算新头部位置
 * 3. 将新头部插入蛇身数组开头
 * 4. 移除尾部最后一节（除非刚吃了食物，由 handleEatFood 处理增长）
 *
 * 注意：此函数不进行碰撞检测，碰撞检测在 TICK action 中单独处理。
 *
 * @param state - 当前游戏状态
 * @returns 移动后的新游戏状态
 */
export function moveSnake(state: GameState): GameState {
  const { snake } = state;
  const direction = snake.nextDirection;
  const head = snake.body[0];

  const newHead: Position = {
    x: head.x + direction.x,
    y: head.y + direction.y,
  };

  // 移除尾部（除非刚吃了食物，由外部处理增长）
  const newBody = [newHead, ...snake.body.slice(0, -1)];

  return {
    ...state,
    snake: {
      body: newBody,
      direction,
      nextDirection: direction,
    },
  };
}

/**
 * 穿墙传送：将越界的头部坐标环绕到对面。
 *
 * 当穿墙护盾激活时，蛇头超出边界后从对面出现：
 * - 左边界外 → 从右边界内出现
 * - 右边界外 → 从左边界内出现
 * - 上边界外 → 从下边界内出现
 * - 下边界外 → 从上边界内出现
 *
 * 如果头部未越界，原样返回状态。
 *
 * @param state - 当前游戏状态
 * @returns 传送后的新游戏状态（或原状态如果未越界）
 */
export function wrapPosition(state: GameState): GameState {
  const head = state.snake.body[0];
  const { width, height } = state.grid;

  let wrappedX = head.x;
  let wrappedY = head.y;

  if (head.x < 0) wrappedX = width - 1;
  if (head.x >= width) wrappedX = 0;
  if (head.y < 0) wrappedY = height - 1;
  if (head.y >= height) wrappedY = 0;

  if (wrappedX !== head.x || wrappedY !== head.y) {
    const newBody = [{ x: wrappedX, y: wrappedY }, ...state.snake.body.slice(1)];
    return {
      ...state,
      snake: {
        ...state.snake,
        body: newBody,
      },
    };
  }

  return state;
}

// ============================================================================
// 食物收集处理
// ============================================================================

/**
 * 处理吃到食物的逻辑：增长蛇身、更新得分、生成新食物、生成粒子。
 *
 * 得分计算：
 * - 基础分：GAME_CONFIG.foodScore（10 分）
 * - 连击加分：(连击数 - 1) × comboBonusBase（5 分）
 * - 双倍分数效果：总得分 × 2
 *
 * 连击判定：
 * - 若上次吃食物时间在 comboWindow（3 秒）内，连击数 +1
 * - 否则连击数重置为 1
 *
 * @param state - 当前游戏状态
 * @returns 处理后的新游戏状态
 */
export function handleEatFood(state: GameState): GameState {
  const now = Date.now();
  const { score, snake, food, grid } = state;

  // 连击判定
  const timeSinceLastEat = now - score.lastEatTime;
  const isCombo = timeSinceLastEat < GAME_CONFIG.comboWindow && score.lastEatTime > 0;
  const newCombo = isCombo ? score.combo + 1 : 1;

  // 计算得分
  const hasDoubleScore = state.activeEffects.some(
    (e) => e.type === PowerUpType.DoubleScore
  );
  const baseScore = GAME_CONFIG.foodScore;
  const comboBonus = (newCombo - 1) * GAME_CONFIG.comboBonusBase;
  const totalScore = (baseScore + comboBonus) * (hasDoubleScore ? 2 : 1);

  const newCurrentScore = score.current + totalScore;
  const newHighScore = Math.max(newCurrentScore, score.highScore);

  // 持久化最高分
  if (newHighScore > score.highScore) {
    saveHighScore(newHighScore);
  }

    // 蛇身增长：在尾部追加一节（复制尾部最后一节）
  const tail = snake.body[snake.body.length - 1];
  const newBody = [...snake.body, { ...tail }];

  // 先生成障碍物（需要先确定障碍物位置，以便后续食物生成时避开）
  const newObstacles = generateRandomObstacles(
    GAME_CONFIG.obstaclesPerEat,
    newBody,
    food.position, // 使用旧食物位置（新食物尚未生成）
    state.powerUpItem?.position ?? null,
    state.obstacles,
    grid
  );

  // 生成新食物（避开蛇身和所有障碍物，包括新生成的）
  const newFood = generateRandomFood(
    newBody,
    grid,
    [...state.obstacles, ...newObstacles]
  );

  // 判断设备类型
  const isMobile =
    typeof window !== 'undefined' &&
    window.innerWidth < GAME_CONFIG.mobileBreakpoint;

  // 生成食物收集粒子
  const eatParticles = spawnParticles(
    food.position.x,
    food.position.y,
    COLOR_SCHEME.food,
    randomInt(
      GAME_CONFIG.eatParticleCount[0],
      GAME_CONFIG.eatParticleCount[1]
    ),
    isMobile
  );

    // 合并并限制粒子总数
  const combinedParticles = limitParticles(
    [...state.particles, ...eatParticles],
    isMobile
  );

  return {
    ...state,
    snake: {
      ...state.snake,
      body: newBody,
    },
    food: newFood,
    score: {
      current: newCurrentScore,
      highScore: newHighScore,
      combo: newCombo,
      lastEatTime: now,
    },
    particles: combinedParticles,
    obstacles: [...state.obstacles, ...newObstacles],
  };
}

// ============================================================================
// 道具收集处理
// ============================================================================

/**
 * 处理收集道具的逻辑：激活效果，移除场景道具，生成粒子。
 *
 * 道具效果处理：
 * - **缩小药剂**：立即生效，缩短蛇身指定节数（最少保留 minSnakeLength 节）
 * - **其他道具**：加入 activeEffects 列表，设置持续时间
 *   - 若已有同类型效果，刷新持续时间
 *   - 若效果数量已达上限，移除最旧的效果
 *
 * @param state - 当前游戏状态
 * @returns 处理后的新游戏状态
 */
export function handleCollectPowerUp(state: GameState): GameState {
  if (state.powerUpItem === null) {
    return state;
  }

  const { powerUpItem, activeEffects } = state;
  const { type } = powerUpItem;

    // 缩小药剂立即生效，不加入 activeEffects
  if (type === PowerUpType.Shrink) {
    const shrinkAmount = GAME_CONFIG.shrinkAmount;
    const minLength = GAME_CONFIG.minSnakeLength;
    const currentLength = state.snake.body.length;

    if (currentLength > minLength) {
      const removeCount = Math.min(shrinkAmount, currentLength - minLength);
      const newBody = state.snake.body.slice(0, -removeCount);

      return {
        ...state,
        snake: {
          ...state.snake,
          body: newBody,
        },
        powerUpItem: null,
      };
    }

    // 蛇身已最短，仅移除道具
    return {
      ...state,
      powerUpItem: null,
    };
  }

  // 清除障碍物立即生效：随机清除10个障碍物
  if (type === PowerUpType.ClearObstacles) {
    const clearCount = 10;
    const currentObstacles = state.obstacles;

    if (currentObstacles.length === 0) {
      // 没有障碍物，仅移除道具
      return {
        ...state,
        powerUpItem: null,
      };
    }

    // 随机选择要清除的障碍物（最多 clearCount 个）
    const toRemove = Math.min(clearCount, currentObstacles.length);
    // Fisher-Yates 部分洗牌：随机选取 toRemove 个索引
    const indices = Array.from({ length: currentObstacles.length }, (_, i) => i);
    for (let i = 0; i < toRemove; i++) {
      const j = i + Math.floor(Math.random() * (indices.length - i));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    const removeSet = new Set(indices.slice(0, toRemove));
    const remainingObstacles = currentObstacles.filter(
      (_, index) => !removeSet.has(index)
    );

    return {
      ...state,
      powerUpItem: null,
      obstacles: remainingObstacles,
    };
  }

  // 其他道具：确定持续时间
  let duration: number;
  switch (type) {
    case PowerUpType.Speed:
    case PowerUpType.Slow:
      duration = GAME_CONFIG.speedEffectDuration;
      break;
    case PowerUpType.WallPass:
      duration = GAME_CONFIG.wallPassDuration;
      break;
    case PowerUpType.DoubleScore:
      duration = GAME_CONFIG.doubleScoreDuration;
      break;
    default:
      duration = GAME_CONFIG.speedEffectDuration;
  }

  // 检查是否已有同类型效果，若有则刷新持续时间
  const existingIndex = activeEffects.findIndex((e) => e.type === type);
  let newEffects: ActiveEffect[];

  if (existingIndex >= 0) {
    newEffects = [...activeEffects];
    newEffects[existingIndex] = { type, remainingTime: duration };
  } else {
    // 检查效果数量上限
    if (activeEffects.length >= GAME_CONFIG.maxActiveEffects) {
      // 移除最旧的效果（索引 0）
      newEffects = [...activeEffects.slice(1), { type, remainingTime: duration }];
    } else {
      newEffects = [...activeEffects, { type, remainingTime: duration }];
    }
  }

  // 判断设备类型
  const isMobile =
    typeof window !== 'undefined' &&
    window.innerWidth < GAME_CONFIG.mobileBreakpoint;

  // 生成道具收集粒子
  const powerUpColor =
    COLOR_SCHEME.powerUpColors[type] || '#ffffff';
  const collectParticles = spawnParticles(
    powerUpItem.position.x,
    powerUpItem.position.y,
    powerUpColor,
    20,
    isMobile
  );

  // 合并并限制粒子总数
  const combinedParticles = limitParticles(
    [...state.particles, ...collectParticles],
    isMobile
  );

  return {
    ...state,
    powerUpItem: null,
    activeEffects: newEffects,
    particles: combinedParticles,
  };
}

// ============================================================================
// 道具效果管理
// ============================================================================

/**
 * 更新激活效果的剩余时间，移除已过期的效果。
 *
 * 对每个激活效果：
 * 1. 减少 remainingTime（减去 deltaTime）
 * 2. 过滤掉 remainingTime <= 0 的效果
 *
 * 如果效果列表无变化，返回原状态以避免不必要的重渲染。
 *
 * @param state - 当前游戏状态
 * @param deltaTime - 时间增量（毫秒）
 * @returns 更新后的游戏状态
 */
export function updateActiveEffects(state: GameState, deltaTime: number): GameState {
  if (state.activeEffects.length === 0) {
    return state;
  }

  const updatedEffects = state.activeEffects
    .map((effect) => ({
      ...effect,
      remainingTime: effect.remainingTime - deltaTime,
    }))
    .filter((effect) => effect.remainingTime > 0);

  // 如果效果列表无变化，返回原状态
  if (updatedEffects.length === state.activeEffects.length) {
    return state;
  }

  return {
    ...state,
    activeEffects: updatedEffects,
  };
}

/**
 * 检查场景中的道具是否过期。
 *
 * 道具在生成后经过 powerUpLifetime（15 秒）自动消失。
 * 使用 Date.now() 与 spawnTime 的差值判断。
 *
 * @param state - 当前游戏状态
 * @returns 更新后的游戏状态（如果道具过期则移除）
 */
export function checkPowerUpExpiry(state: GameState): GameState {
  if (state.powerUpItem === null) {
    return state;
  }

  const elapsed = Date.now() - state.powerUpItem.spawnTime;
  if (elapsed >= GAME_CONFIG.powerUpLifetime) {
    return {
      ...state,
      powerUpItem: null,
    };
  }

  return state;
}

// ============================================================================
// 游戏结束处理
// ============================================================================

/**
 * 处理游戏结束：更新状态、生成死亡粒子、更新最高分。
 *
 * 游戏结束流程：
 * 1. 更新最高分（如果当前得分更高）
 * 2. 持久化最高分到 localStorage
 * 3. 为蛇身每个节段生成碎裂粒子
 * 4. 将状态切换为 GameOver
 *
 * @param state - 当前游戏状态
 * @returns 游戏结束状态
 */
export function handleGameOver(state: GameState): GameState {
  const newHighScore = Math.max(state.score.current, state.score.highScore);
  if (newHighScore > state.score.highScore) {
    saveHighScore(newHighScore);
  }

  // 判断设备类型
  const isMobile =
    typeof window !== 'undefined' &&
    window.innerWidth < GAME_CONFIG.mobileBreakpoint;

  // 生成蛇身碎裂粒子
  const deathParticles = spawnDeathParticles(
    state.snake.body,
    state.score.current,
    isMobile
  );

  // 合并并限制粒子总数
  const combinedParticles = limitParticles(
    [...state.particles, ...deathParticles],
    isMobile
  );

  return {
    ...state,
    status: GameStatus.GameOver,
    score: {
      ...state.score,
      highScore: newHighScore,
    },
    particles: combinedParticles,
  };
}

// ============================================================================
// 游戏状态 Reducer
// ============================================================================

/**
 * 游戏状态纯函数 Reducer。
 *
 * 处理所有 GameAction 类型，返回新的不可变状态。
 * 使用 switch-case 进行可辨识联合的类型收窄。
 * default 分支使用 never 类型确保穷尽性检查。
 *
 * @param state - 当前游戏状态
 * @param action - 分发的 GameAction
 * @returns 新的游戏状态
 */
export function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    // --------------------------------------------------------------------
    // START_GAME: 将状态从 Idle 或 GameOver 切换到 Playing
    // --------------------------------------------------------------------
    case 'START_GAME': {
      if (
        state.status === GameStatus.Idle ||
        state.status === GameStatus.GameOver
      ) {
        const newState = createInitialState();
        return {
          ...newState,
          status: GameStatus.Playing,
          score: {
            ...newState.score,
            highScore: state.score.highScore, // 保留历史最高分
          },
        };
      }
      return state;
    }

    // --------------------------------------------------------------------
    // SET_DIRECTION: 更新缓冲方向（防止反向移动）
    // --------------------------------------------------------------------
    case 'SET_DIRECTION': {
      if (state.status !== GameStatus.Playing) {
        return state;
      }

      const { direction } = action;
      const currentDirection = state.snake.direction;

      // 禁止反向移动：新方向不能与当前实际移动方向相反
      if (isOppositeDirection(direction, currentDirection)) {
        return state;
      }

      // 禁止原地不动（零向量）
      if (direction.x === 0 && direction.y === 0) {
        return state;
      }

      return {
        ...state,
        snake: {
          ...state.snake,
          nextDirection: direction,
        },
      };
    }

    // --------------------------------------------------------------------
    // TICK: 游戏主循环逻辑
    // --------------------------------------------------------------------
    case 'TICK': {
      if (state.status !== GameStatus.Playing) {
        return state;
      }

      let newState = { ...state };

      // --- 更新道具效果计时器 ---
      newState = updateActiveEffects(newState, action.deltaTime);

      // --- 检查道具是否过期 ---
      newState = checkPowerUpExpiry(newState);

      // --- 移动蛇 ---
      newState = moveSnake(newState);

      // --- 碰撞检测 ---
      const head = newState.snake.body[0];

      // 墙壁碰撞检测（若无穿墙效果）
      const hasWallPass = newState.activeEffects.some(
        (e) => e.type === PowerUpType.WallPass
      );

      if (!hasWallPass) {
        if (checkWallCollision(head, newState.grid)) {
          return handleGameOver(newState);
        }
      } else {
        // 穿墙：将头部传送到对面
        newState = wrapPosition(newState);
      }

            // 自身碰撞检测
      if (checkSelfCollision(newState.snake.body[0], newState.snake.body)) {
        return handleGameOver(newState);
      }

      // 障碍物碰撞检测：碰到障碍物失去一个生命
      if (
        newState.obstacles.length > 0 &&
        checkObstacleCollision(newState.snake.body[0], newState.obstacles)
      ) {
        const newLives = newState.lives - 1;
        if (newLives <= 0) {
          // 生命归零，游戏结束
          return handleGameOver(newState);
        }
        // 失去一个生命，移除碰到的障碍物，重置蛇位置
        const collidedObstaclePos = newState.obstacles.find((obs) =>
          positionsEqual(newState.snake.body[0], obs.position)
        );
        return {
          ...newState,
          lives: newLives,
          obstacles: newState.obstacles.filter(
            (obs) => !positionsEqual(obs.position, collidedObstaclePos!.position)
          ),
          // 重置蛇到初始位置（保持当前长度和方向）
          snake: {
            ...newState.snake,
            body: createInitialState().snake.body,
          },
        };
      }

      // --- 食物碰撞检测 ---
      if (checkFoodCollision(newState.snake.body[0], newState.food)) {
        newState = handleEatFood(newState);
      }

      // --- 道具碰撞检测 ---
      if (checkPowerUpCollision(newState.snake.body[0], newState.powerUpItem)) {
        newState = handleCollectPowerUp(newState);
      }

      return newState;
    }

    // --------------------------------------------------------------------
    // RESET: 重置游戏到初始状态
    // --------------------------------------------------------------------
    case 'RESET': {
      const newState = createInitialState();
      return {
        ...newState,
        score: {
          ...newState.score,
          highScore: state.score.highScore, // 保留历史最高分
        },
      };
    }

    // --------------------------------------------------------------------
    // UPDATE_PARTICLES: 更新粒子生命周期和位置
    // --------------------------------------------------------------------
    case 'UPDATE_PARTICLES': {
      const { deltaTime } = action;
      const updatedParticles = state.particles
        .map((p) => ({
          ...p,
          x: p.x + p.vx * deltaTime,
          y: p.y + p.vy * deltaTime,
          life: p.life - deltaTime,
        }))
        .filter((p) => p.life > 0);

      return {
        ...state,
        particles: updatedParticles,
      };
    }

    // --------------------------------------------------------------------
    // SPAWN_POWERUP: 在场景中生成道具
    // --------------------------------------------------------------------
    case 'SPAWN_POWERUP': {
      return {
        ...state,
        powerUpItem: action.powerUp,
      };
    }

    // --------------------------------------------------------------------
    // COLLECT_POWERUP: 收集道具并激活效果
    // --------------------------------------------------------------------
    case 'COLLECT_POWERUP': {
      return handleCollectPowerUp(state);
    }

    // --------------------------------------------------------------------
    // UPDATE_EFFECTS: 更新道具效果计时器
    // --------------------------------------------------------------------
    case 'UPDATE_EFFECTS': {
      return updateActiveEffects(state, action.deltaTime);
    }

    default: {
      // 穷尽性检查：确保所有 Action 类型都已处理
      const _exhaustive: never = action;
      void _exhaustive;
      return state;
    }
  }
}